<template>
  <div v-hotkey="hotkeyBinding"
       id="myapp"
       :class="[breakpoint, { 'high-contrast': highContrast }, { 'touch' : touchOnly }, { 'dashboard--nav-wide' : widenav }, {'dashboard--screenshot-mode': screenshotMode }, {'dashboard--new-version-pending': !isNil(newVersionPending)}, [layoutClass], { 'role--sysadmin': hasRole('ROLE_SYSADMIN') }]"
       @click="onClick">
    <template v-if="settingsFullyLoaded">
      <router-view ref="nav" name="nav" @widenav="onwidenav"></router-view>
      <router-view name="header"></router-view>
      <div class="main-shadow"></div>
      <loading-wrapper>
        <resize-sensor @resize="onresize"></resize-sensor>
        <transition-fade-page :duration="0.2">
          <router-view :key="$route.fullPath"></router-view>
        </transition-fade-page>
        <div class="splash__support text-center" style="display:none;">
          <div class="iframe__support-wrapper">
            <button type="button" class="close" onclick="closeSupportDialog();">X</button>
            <iframe id="freshwidget-embedded-form"
                    :title="$t('myApp.supportForm.title')"
                    class="freshwidget-embedded-form iframe__support"
                    src="about:blank"
                    scrolling="no"
                    frameborder="0"
                    style="width:100%;height: 600px;">
            </iframe>
          </div>
        </div>

        <span v-if="showChatbot" class="button--chat" @click="showChat"><i class="fa-sharp fa-regular fa-messages"></i></span>

        <span class="button--docs" @click="showDocumentation">?</span>

        <span v-if="showZendesk" id="support" class="button--support" @click="showSupportDialog">
          {{ $t('myApp.support') }}
        </span>
        <span v-else-if="showMailToSupport" class="button--support" @click="onClickMailToSupport">
          {{ $t('myApp.support') }}
        </span>
      </loading-wrapper>
      <modal-user-settings />
    </template>
    <notifications ref="notifications"
                   :duration="5000"
                   :animation="notificationAnimation"
                   position="top right"
                   group="msg"
                   animation-type="velocity">
      <template slot="body" slot-scope="props">
        <div :class="[props.item.type]" class="vue-notification-template vue-notification" @click="$refs.notifications.destroy(props.item)">
          <div :class="{'notification-title--no-content': props.item.text == null || props.item.text === ''}" class="notification-title" v-html="props.item.title"></div>
          <div class="notification-content" v-html="props.item.text"></div>
          <div :style="`animation-duration: ${props.item.length + props.item.speed}ms`" class="notification-progress" />
        </div>
      </template>
    </notifications>
    <modal-message-bus />
    <printer />
    <div v-if="offline" id="offline">{{ $t('myApp.offline') }}</div>
    <keyboard-shortcuts-overlay />
    <speedtest />
    <documentation />
    <support-form />
    <chat-form />

    <script v-if="hasLicense('InterfaceSeam')"
            type="module"
            src="https://react.seam.co/v/1.42.1/dist/elements.js"
    ></script>
  </div>
</template>

<script>
/* eslint-disable no-undef */
/* eslint func-names: 0 */

import { clone, debounce, isEmpty, isNil, throttle } from 'lodash';
import Vue from 'vue';
import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import LoadingWrapper from './lib/util/LoadingWrapper';
import Modal from './lib/util/Modal';
import TransitionFade from '../transitions/TransitionFade';
import ModalMessageBus from './lib/util/ModalMessageBus';
import KeyboardShortcutsOverlay from './lib/util/KeyboardShortcutsOverlay';
import UpdateHotelSettingsMixin from '../mixins/UpdateHotelSettingsMixin';
import TimerMixin from '../mixins/TimerMixin';
import { getToken, setToken, clearToken } from '../lib/util/token';
import ResizeSensor from 'vue-resize-sensor';
import WebSocketMixin from '../mixins/WebSocketMixin';

export default {
  name: 'MyApp',
  components: { KeyboardShortcutsOverlay, ModalMessageBus, TransitionFade, LoadingWrapper, ResizeSensor },
  mixins: [HotelmeisterMixin, UpdateHotelSettingsMixin, TimerMixin, WebSocketMixin],

  data() {
    return {
      // ctrl+t, ctrl+n, ctrl+shift+t, ctrl+shift+n, ctrl+w do only work if installed as app
      wsConnecting: false,
      wsInitialized: false,
      screenshotMode: false,
      offline: false,
      newVersionPending: null,
      highContrast: null,
      innerWidth: 0,
      styleNode: null,
      hotkeyBinding: {
        esc: ($event) => { this.$serviceBus.$emit('shortcut.esc'); }, // DE keyboard layout
        'shift+ß': ($event) => { if ($event.target.nodeName !== 'INPUT' && $event.target.nodeName !== 'TEXTAREA') this.$serviceBus.$emit('shortcut.help'); }, // DE keyboard layout
        'shift+/': ($event) => { if ($event.target.nodeName !== 'INPUT' && $event.target.nodeName !== 'TEXTAREA') this.$serviceBus.$emit('shortcut.help'); }, // EN keyboard layout
        'ctrl+t': this.$root.openInNewTab,
        'alt+t': this.$root.openInNewTab,
        'meta+t': this.$root.openInNewTab,
        f1: this.showHelp,
        // protel air
        'alt+u': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.editGuest'); },
        'alt+i': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.editFolios'); },
        'alt+o': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.editReservation'); },
        'ctrl+n1': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.createGuest'); },
        'ctrl+n2': ($event) => { $event.preventDefault(); this.$router.push({ name: 'createReservation', params: { sameWindow: true } }); },
        'ctrl+numpad 2': ($event) => { $event.preventDefault(); this.$router.push({ name: 'createReservation', params: { sameWindow: true } }); },
        'ctrl+alt+g': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.universalSearch'); },
        // gastrodat
        f2: ($event) => { $event.preventDefault(); this.$router.push({ name: 'home' }); },
        f3: ($event) => { $event.preventDefault(); this.$router.push({ name: 'roomplan' }); },
        // 'ctrl+l': ($event) => { $event.preventDefault(); this.$router.push({ name: 'roomplan' }); }, // fidelio // not possible - reserved for address bar
        f4: ($event) => { $event.preventDefault(); this.$router.push({ name: 'findReservation' }); },
        // 'ctrl+a': ($event) => { $event.preventDefault(); this.$router.push({ name: 'findReservation' }); }, // fidelio -arrivals // not possible - reserved for select all
        'ctrl+i': ($event) => { $event.preventDefault(); this.$router.push({ name: 'findReservation' }); }, // fidelio -in house
        f6: ($event) => { $event.preventDefault(); this.$router.push({ name: 'findInquiry' }); },
        f7: ($event) => { $event.preventDefault(); this.$router.push({ name: 'findOffer' }); },
        f9: ($event) => { $event.preventDefault(); this.$router.push({ name: 'findGuest' }); },
        // 'ctrl+p': ($event) => { $event.preventDefault(); this.$router.push({ name: 'findGuest' }); }, // fidelio - guest profiles // not possible reserved for print
        f10: ($event) => { $event.preventDefault(); this.$router.push({ name: 'createReservation' }); },
        // 'ctrl+n': ($event) => { $event.preventDefault(); this.$router.push({ name: 'createReservation' }); }, // fidelio - new reservation // not possible - reserved for new window
        // 'ctrl+d': ($event) => { $event.preventDefault(); this.$router.push({ name: 'createReservation' }); }, // fidelio - room availability // not possible - reserved for bookmark
        // 'ctrl+r': ($event) => { $event.preventDefault(); this.$router.push({ name: 'createReservation' }); }, // fidelio - rate availability // not possible - reserved for reload page
        // gastrodat
        'alt+a': ($event) => { $event.preventDefault(); this.$router.push({ name: 'createReservation' }); },
        'alt+b': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.checkIn'); },
        // 'alt+o': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.checkOut'); },
        'alt+r': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.createInvoice'); }, // was ctrl but ctrl+r is reload
        'alt+n0': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.count', 0); },
        'alt+n1': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.count', 1); },
        'alt+n2': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.count', 2); },
        'alt+n3': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.count', 3); },
        'alt+n4': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.count', 4); },
        'alt+n5': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.count', 5); },
        'alt+n6': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.count', 6); },
        'alt+n7': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.count', 7); },
        'alt+n8': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.count', 8); },
        'alt+n9': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.count', 9); },
        'alt+numpad 0': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.count', 0); },
        'alt+numpad 1': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.count', 1); },
        'alt+numpad 2': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.count', 2); },
        'alt+numpad 3': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.count', 3); },
        'alt+numpad 4': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.count', 4); },
        'alt+numpad 5': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.count', 5); },
        'alt+numpad 6': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.count', 6); },
        'alt+numpad 7': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.count', 7); },
        'alt+numpad 8': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.count', 8); },
        'alt+numpad 9': ($event) => { $event.preventDefault(); this.$serviceBus.$emit('shortcut.count', 9); },
        right: ($event) => { this.$serviceBus.$emit('shortcut.right', $event); },
        left: ($event) => { this.$serviceBus.$emit('shortcut.left', $event); },
        up: ($event) => { this.$serviceBus.$emit('shortcut.up', $event); },
        down: ($event) => { this.$serviceBus.$emit('shortcut.down', $event); },
        'ctrl+right': ($event) => { this.$serviceBus.$emit('shortcut.pageRight', $event); },
        'ctrl+left': ($event) => { this.$serviceBus.$emit('shortcut.pageLeft', $event); },
        'ctrl+up': ($event) => { this.$serviceBus.$emit('shortcut.pageUp', $event); },
        'ctrl+down': ($event) => { this.$serviceBus.$emit('shortcut.pageDown', $event); },
        'ctrl+home': ($event) => { this.$serviceBus.$emit('shortcut.scrollTop', $event); },
        enter: ($event) => { this.$serviceBus.$emit('shortcut.enter', $event); },
        return: ($event) => { this.$serviceBus.$emit('shortcut.return', $event); },
        tab: ($event) => { this.$serviceBus.$emit('shortcut.tab', $event); },
        'ctrl+alt+s': ($event) => { this.screenshotMode = this.hasRole('ROLE_SYSADMIN') ? !this.screenshotMode : false; },
        // 'ctrl+end': ($event) => { this.$serviceBus.$emit('shortcut.scrollBottom'); },
      },
      widenav: false,
      notificationAnimation: {
        enter: el => ({
          right: [0, -300],
          scale: [1, 0.2],
          opacity: [1, 0],
        }),
        leave: {
          right: -300,
          opacity: [0, 1],
        },
      },
    };
  },

  computed: {
    ws: function () {
      return this.$store.getters['hotel/webSocket'];
    },
    showChatbot: function () {
      return !isNil(this.hotelSettings) && !isNil(this.hotelSettings.hotel) && ['Hotelmeister'].includes(this.hotelSettings.hotel.whiteLabel);
    },
    showZendesk: function () {
      return !isNil(this.hotelSettings) && !isNil(this.hotelSettings.hotel) && ['Hotelmeister'].includes(this.hotelSettings.hotel.whiteLabel);
    },
    showMailToSupport: function () {
      return !isNil(this.hotelSettings) && !isNil(this.hotelSettings.hotel) && ['Rainmaker'].includes(this.hotelSettings.hotel.whiteLabel);
    },
    breakpoint: function () {
      return this.$store.getters['layout/activeBreakpoint'];
    },
    touchOnly: function () {
      return this.$store.getters['layout/touchOnly'];
    },
    layoutClass: function () {
      return this.$route.name === 'login' ? 'login' : 'dashboard';
    },
  },

  watch: {
    ws: function (newVal) {
      if (isNil(newVal)) this.wsReconnect();
    },
    innerWidth: function (newVal) {
      if (!isNil(this.styleNode)) {
        if (newVal > 0) {
          const margin = Math.ceil(this.innerWidth / 29.7);
          this.styleNode.textContent = `
            @media print {
              @page {
                size: ${this.innerWidth}px ${Math.ceil(this.innerWidth / 29.7 * 21.0)}px!important;
              }
            }
          `;
        } else {
          this.styleNode.textContent = '';
        }
      }
    },
    layoutClass: function (newVal, oldVal) {
      if (oldVal === 'login') {
        // this.init();
      }
    },
    '$root.username': function (newVal) {
      if (isEmpty(newVal)) {
        this.wsDisconnect();
      } else if (!isNil(this.ws) && this.ws.readyState === 1) {
        this.wsInit();
      } else {
        this.wsConnect();
      }
    },
    '$root.hotel': function (newVal, oldVal) {
      if (isNil(newVal)) {
        this.$store.dispatch('hotel/logout');
        this.wsDisconnect();
      } else {
        if (!isEmpty(oldVal)) this.wsDisconnect();
        if (!isEmpty(newVal)) {
          this.$nextTick(() => {
            this.wsConnect();
          });
        }
      }
      this.init(newVal);
    },
    'hotelSettings.hotel.hotelName': function () {
      if (!isNil(this.hotelSettings) && !isNil(this.hotelSettings.hotel) && !isEmpty(this.hotelSettings.hotel.hotelName)) {
        document.title = this.hotelSettings.hotel.hotelName;
      }
    },
    'hotelSettings.hotel.timeZone': function (newVal) {
      if (!isEmpty(newVal)) {
        /*
         setting users local time zone instead (in src/lib/util/moment.js), because otherwise we might be getting dates a full day off
         example:
         * 20211023 is set as arrivalDate
         * moment default timezone is set to Europe/Vienna
         * date object created is 23.10.2021 00:00:00 GMT+0200 CEST
         * when being formatted with Intl.DateTimeFormat the browser is using the system timezone (e.g. Atlantic/Canary)
         * therefore the output date object is 22.10.2021 23:00:00 GMT+0100 WEST
         * the resulting date is 22.10.2021
        */
        // this.$moment.tz.setDefault(newVal);
      }
    },
  },

  created: function () {
    this.$serviceBus.$on('ui.ws.onopen', this.wsInit);
    // this.$serviceBus.$on('ui.ws.onmessage', this.onmessage);
    this.$serviceBus.$on('ui.ws.onclose', this.wsDisconnected);
    this.$serviceBus.$on('ui.ws.onerror', this.wsOnError);

    this.highContrast = JSON.parse(this.getUserConfig('highContrast', true)) || false;

    this.offline = window.hmOffline === true;
    window.onHmOnline = () => { this.offline = window.hmOffline === true; };
    window.onHmOffline = () => { this.offline = window.hmOffline === true; };
    window.newVersion = (registration) => {
      this.newVersionPending = registration;
      this.showModal(
        this.$t('myApp.confirmLoadNewVersion'),
        () => {
          this.updateNewVersion();
        },
      );
    };

    this.debouncedUpdateHotelSettings = debounce(this.updateHotelSettings, 5000);
    this.$serviceBus.$on('hotelSettings.update', this.debouncedUpdateHotelSettings);
    this.$serviceBus.$on('hotelSettings.update.immediate', this.updateHotelSettings);
    this.$serviceBus.$on('menu.updateNewVersion', this.updateNewVersion);
    this.$serviceBus.$on('update:fontSize', this.updateFontSize);
    this.$serviceBus.$on('update:highContrast', this.updateHighContrast);

    window.addEventListener('keyup', this.keyup);
    window.addEventListener('keydown', this.keydown);
    window.addEventListener('rageclick', this.rageclick);

    this.setInterval(() => {
      if (!isNil(this.$root.hotel)) {
        this.$store.dispatch(
          'notifications/initialiseStore',
          this.axios.get(this.serviceUrls.hotel.notifications(this)),
        );
      }
    }, 1000 * 60 * 5);

    let hotel = null;

    if (isNil(this.$root.hotel) && !isNil(this.$route.params.hotelId)) {
      hotel = this.$route.params.hotelId;
    }

    if (isNil(getToken(hotel)) && !isNil(this.$route.params.token)) {
      setToken(hotel, this.$route.params.token);
    }

    const token = getToken(hotel);

    if (!isNil(hotel) && !isEmpty(token)) {
      if (hotel !== this.$root.hotel) this.$store.dispatch('hotel/unloadHotel');
      this.$root.testToken()
        .then((response) => {
          this.init(hotel);
        });
    } else {
      this.$root.logout();
    }

    if (!isNil(this.$root.hotel) && !isNil(getToken(this.$root.hotel))) {
      this.$store.dispatch('hotel/updateUser')
        .then((userResponse) => {
          const user = userResponse.data.data;

          this.$rollbar.configure({
            payload: {
              person: {
                id: user.id,
                username: `${user.name}@${user.tenantSubdomain}`,
                email: user.email,
              },
            },
          });

          this.$root.updateHotels();
        });
    }

    if (!isNil(hotel) && !isEmpty(token)) {
      this.wsConnect();
    }

    try { this.$rollbar.configure({ payload: { locale: this.$i18n.locale } }); } catch (ignore) { /* ignore */ }

    /*
    this.setInterval(() => {
      $vm.showErrorMessage('Das ist eine ziemlich lange Fehlermeldung, die wir so von einer Schnittstelle erhalten könnten.', 'Fehler');
      $vm.showWarningMessage('Das ist eine ziemlich lange Fehlermeldung, die wir so von einer Schnittstelle erhalten könnten.', 'Fehler');
      $vm.showSuccessMessage();
      $vm.showWarningMessage();
      $vm.showErrorMessage();
    }, 5000);
     */
    // this.showOfflineMessage();
  },

  beforeDestroy() {
    this.$serviceBus.$off('hotelSettings.update', this.debouncedUpdateHotelSettings);
    this.$serviceBus.$off('hotelSettings.update.immediate', this.updateHotelSettings);
    // this.$serviceBus.$off('menu.updateNewVersion', this.updateNewVersion);
    this.$serviceBus.$off('update:fontSize', this.updateFontSize);
    this.$serviceBus.$off('ui.ws.onopen', this.onopen);
    this.$serviceBus.$off('ui.ws.onmessage', this.onmessage);
    this.$serviceBus.$off('ui.ws.onclose', this.onclose);
    this.$serviceBus.$off('ui.ws.onerror', this.onerror);

    if (!isNil(this.styleNode)) {
      this.styleNode.textContent = '';
    }
  },

  destroyed: function () {
    window.removeEventListener('keyup', this.keyup);
    window.removeEventListener('keydown', this.keydown);
    window.removeEventListener('rageclick', this.rageclick);
  },

  mounted: function () {
    this.$serviceBus.$on('navigatedTo', (defaultComponentName) => {
      switch (defaultComponentName) {
        case 'PageLogin': this.helpMessage('Anmeldung');
          break;
        case 'PageFrontDesk': this.helpMessage('Anreise');
          break;
        case 'PageCockpit': this.helpMessage('Statistik');
          break;
        case 'PageCreateReservation':
        case 'PageDraftReservation':
          this.helpMessage('Reservierung erstellen');
          break;
        case 'PageEmail':
          this.helpMessage('E-Mail');
          break;
        case 'PageFindGuestOffer':
        case 'PageGuestOffer':
          this.helpMessage('Angebot');
          break;
        case 'PageFolio':
        case 'PageFolios':
          this.helpMessage('Rechnung');
          break;
        case 'PageFindHotelInquiry':
        case 'PageHotelInquiry':
          this.helpMessage('Anfrage');
          break;
        case 'PageFindGuestProfile':
        case 'PageGuestProfile':
          this.helpMessage('Gästeprofil');
          break;
        case 'PageFindRoomReservation':
        case 'PageRoomReservation':
          this.helpMessage('Zimmerreservierung');
          break;
        case 'PageReservationGroup':
          this.helpMessage('Gruppenreservierung');
          break;
        case 'PageMeldeblatt':
          this.helpMessage('Meldeblatt');
          break;
        case 'PageNotifications':
          this.helpMessage('Benachrichtigung');
          break;
        case 'PageRoomplan':
          this.helpMessage('Zimmerplan');
          break;
        default: this.helpMessage('Zimmerreservierung');
      }
    });


    const style = document.createElement('style');
    style.type = 'text/css';
    style.appendChild(document.createTextNode(''));
    this.styleNode = style.childNodes[0]; // a reference I store in the data hash
    document.head.appendChild(style);

    if (this.$store.getters['hotel/historyLength'] < 0) {
      this.$store.commit('hotel/HISTORY_LENGTH', clone(window.history.length));
    }
  },

  methods: {
    wsOnError(event) {
      /* do nothing - for NOW */
    },
    wsReconnect: throttle(
      function () {
        if (isNil(this.ws)) {
          this.wsConnect();
          this.setTimeout(this.wsReconnect, 30 * 1000);
        }
      },
      15 * 1000,
      { leading: true, trailing: false },
    ),
    wsConnect: debounce(function () {
      if (this.wsConnecting) return;
      try {
        this.wsConnecting = true;
        if (!isNil(this.ws) && this.ws.readyState === 1) {
          this.wsInit();
        } else if (isNil(this.ws) || this.ws.readyState >= 2) { // 2 = CLOSING, 3 = CLOSED, 1 = OPEN, 0 = CONNECTING
          try {
            const token = getToken(this.$root.hotel);
            if (isEmpty(token)) {
              this.wsReconnect();
              return;
            }

            const url = `${process.env.X_WS_URL}?apiKey=${token}&version=${process.env.DATE}&system=ui`;
            const ws = new WebSocket(url);
            ws.onopen = (e) => {
              this.$serviceBus.$emit('ui.ws.onopen', e);
            };
            ws.onmessage = (e) => {
              this.$serviceBus.$emit('ui.ws.onmessage', e);
            };
            ws.onclose = (e) => {
              this.$serviceBus.$emit('ui.ws.onclose', e);
            };
            ws.onerror = (e) => {
              this.$serviceBus.$emit('ui.ws.onerror', e);
            };

            this.$store.commit('hotel/WEBSOCKET', ws);
          } catch (e) {
            this.wsReconnect();
          }
        }
      } catch (e) {
        this.$rollbar.warn(e);
      } finally { this.wsConnecting = false; }
    }, 15 * 1000, { leading: true }),
    wsDisconnect() {
      if (!isNil(this.ws)) {
        try {
          this.ws.close();
        } catch (e) { /* ignore */ }
        this.wsDisconnected();
      }
    },
    wsDisconnected() {
      if (!isNil(this.ws)) {
        if (this.wsConnecting) return;
        if (this.ws.readyState <= 1) return;

        this.$store.commit('hotel/WEBSOCKET', null);
        this.$store.commit('hotel/WEBSOCKET_INITIALIZED', false);
      }
    },
    wsInit() {
      if (!isEmpty(this.$root.username) && this.$store.getters['hotel/webSocketInitialized'] !== true) {
        this.wsSend({ action: 'ui.pair', apiToken: window.getToken(this.$root.hotel), hotelId: this.$root.hotel, tabId: this.$root.tabId });
        this.$store.commit('hotel/WEBSOCKET_INITIALIZED', true);
      }
    },
    rageclick($event) {
      try {
        if (this.$route && this.$route.name === 'login' && $event.detail.targetPath && $event.detail.targetPath.includes('#password-input')) return;
        if (this.$route && this.$route.name === 'login') return;
        if (this.$route && this.$route.name === 'roomplan' && $event.detail.targetPath && $event.detail.targetPath.includes('[type="checkbox"]')) return;
        if (this.$route && this.$route.name === 'roomplan' && $event.detail.targetPath && $event.detail.targetPath.includes('checkbox')) return;
        if ($event.detail.targetPath && $event.detail.targetPath.includes('.hm-input') && $event.detail.targetPath.includes('hasContent')) return;
      } catch (e) { /* ignore */ }

      const metadata = {
        targetPath: $event.detail.targetPath,
        url: window.location.href,
        hotel: this.$root.hotel,
        user: this.$root.username,
      };

      try {
        metadata.subdomain = this.$store.getters['hotel/hotelSettings'].hotel.tenant.subdomain;
      } catch (ex) { /* ignore */ }

      this.$rollbar.captureEvent('manual', {
        category: 'UI',
        action: 'Rage Click',
        metadata: {
          ...metadata,
        },
      }, 'info');

      this.$rollbar.warn('Rage Click');
    },
    keydown($event) {
      if (!isNil($event) && $event.which === 17) this.$serviceBus.$emit('keydown.ctrl');
    },
    keyup($event) {
      if (!isNil($event) && $event.which === 17) this.$serviceBus.$emit('keyup.ctrl');
    },
    showHelp($event) {
      $event.preventDefault();
      if (this.$driver.isActivated) return;
      this.$serviceBus.$emit('help.input.show');
      this.setTimeout(() => {
        if (!this.$driver.isActivated) this.$serviceBus.$emit('help.show');
      }, 200);
    },
    helpMessage(search) {
      this.$serviceBus.$emit('documentation.q', search);
    },
    showSupportDialog() {
      this.$serviceBus.$emit('supportForm.show');
    },
    onwidenav: function (widenav) {
      this.widenav = widenav;
    },
    init: function (hotelId = null) {
      if (isEmpty(hotelId)) {
        this.$store.dispatch('hotel/unloadHotel');
        // this.$store.commit('enums/ENUMS', {});
        // this.$store.commit('enums/LOADED', false);
        this.$store.commit('notifications/NOTIFICATIONS', {
          count: 0,
          read: [],
          unread: [],
        });
        this.$root.logout();
      } else {
        this.$store.dispatch('hotel/setHotelId', hotelId);
        this.$store.dispatch(
          'enums/initialiseStore',
          this.axios.get(this.serviceUrls.enums(this), {
            params: {},
            hotelId: this.$root.hotel,
          }),
        );
        this.$store.dispatch(
          'notifications/initialiseStore',
          this.axios.get(this.serviceUrls.hotel.notifications(this)),
        );
      }
    },
    // updateHotelSettings: function () {
    //   this.$store.dispatch('hotel/setHotelId', this.$root.hotel);
    // },
    updateHotelSettings: function () {
      this.$store.dispatch('hotel/updateHotelSettings');
    },
    updateNewVersion() {
      const registration = this.newVersionPending;
      this.newVersionPending = null;
      if (isNil(registration) || !registration.waiting) {
        // Just to ensure registration.waiting is available before
        // calling postMessage()
        return;
      }
      registration.waiting.postMessage('skipWaiting');
    },
    updateFontSize(newVal) {
      this.setUserConfig('fontSize', newVal || 10, true);
      document.documentElement.style.fontSize = `${newVal || 10}px`;
    },
    updateHighContrast(newVal) {
      this.setUserConfig('highContrast', newVal || false, true);
      this.highContrast = newVal;
    },
    onClick(evt) {
      if (this.screenshotMode) {
        this.highlightClick(evt.x, evt.y);
      }
    },
    highlightClick(x, y) {
      const div = document.createElement('div');
      div.classList.add('screenshot-mode--click');
      div.style.left = `${x}px`;
      div.style.top = `${y}px`;
      document.body.appendChild(div);
      setTimeout(() => {
        div.classList.add('screenshot-mode--click--highlight');
      }, 20);
      setTimeout(() => {
        document.body.removeChild(div);
      }, 400);
    },
    onresize($event) {
      // let offset = 0;
      // try {
      //   offset -= parseFloat(window.getComputedStyle(this.$el.querySelector('main')).getPropertyValue('padding-right'));
      //   offset -= parseFloat(window.getComputedStyle(this.$el.querySelector('main')).getPropertyValue('padding-left'));
      // } catch (_) { /* ignore */ }
      this.innerWidth = $event.width;
    },
    onClickMailToSupport() {
      if (!isNil(this.hotelSettings) && !isNil(this.hotelSettings.hotel) && ['Rainmaker'].includes(this.hotelSettings.hotel.whiteLabel)) {
        switch (this.hotelSettings.hotel.whiteLabel) {
          case 'Rainmaker':
            window.open(`mailto:support-dach@rainmaker.travel?subject=${encodeURIComponent(`Support .frontdesk H (${this.hotelSettings.hotel.hotelName}`)})`, '_blank');
            break;
          default:
            break;
        }
      }
    },
    showDocumentation() {
      this.$serviceBus.$emit('documentation.show');
    },
    showChat() {
      this.$serviceBus.$emit('chatForm.show');
    },
  },
};
</script>

<style lang="scss">
  @import '~styles/main';

  .screenshot-mode {
    &--click {
      position: fixed;
      border-radius: 50%;
      border: 2px solid #2da295;
      background: transparent;
      background: radial-gradient(circle, rgba(255,255,255,0) 0%, rgba(45,162,149,0.25) 100%);
      width: 4px;
      height: 4px;
      transform: translate(-50%, -50%);
      transition: 0.3s ease all;
      z-index: 9999999;
      pointer-events: none;

      &--highlight {
        width: 80px;
        height: 80px;
      }
    }
  }

  #offline {
    position: fixed;
    top: 60px;
    width: 300px;
    height: 35px;
    z-index: 100000;
    background-color: $orange;
    font-weight: bold;
    color: $white;
    margin-left: 50%;
    transform: translateX(-50%);
    border-bottom-right-radius: 0.3rem;
    border-bottom-left-radius: 0.3rem;
    box-shadow: 0 6px 15px 0 rgba(0,39,46,0.55);
    display: flex;
    align-content: center;
    align-items: center;
    justify-content: center;
    justify-items: center;
  }

  .button--docs {
    position: fixed;
    right: 0;
    top: 180px;
    cursor: pointer;
    padding: 1rem;
    background-color: $mint;
    color: #fff;
    text-decoration: none;
    font-weight: bold;
    border-top-left-radius: 0.3rem;
    border-bottom-left-radius: 0.3rem;


    @media (min-width: $br-desktop) {
      padding-left: 1.4rem!important;
      padding-right: 1.4rem!important;
    }

    display: none;
  }

  .button--chat {
    position: fixed;
    right: -10px;
    top: 125px;
    cursor: pointer;
    padding: 1rem;
    background-color: $mint;
    color: #fff;
    text-decoration: none;
    font-weight: bold;
    border-top-left-radius: 0.3rem;
    border-bottom-left-radius: 0.3rem;


    @media (min-width: $br-desktop) {
      padding-left: 1.0rem!important;
      padding-right: 1.8rem!important;
    }

    display: none;
  }

  body.wl-key-hotelmeister {
    .button--docs {
      display: block!important;
    }
    .button--chat {
      display: block!important;
    }
  }

  .button--support {
    position: fixed;
    right: 0;
    top: 235px;
    cursor: pointer;

    -webkit-transform-origin: 100% 100%;
    -ms-transform-origin: 100% 100%;
    transform-origin: 100% 100%;
    -webkit-transform: rotate(-90deg) translateY(2px) translateX(50%);
    -ms-transform: rotate(-90deg) translateY(2px) translateX(50%);
    transform: rotate(-90deg) translateY(2px) translateX(50%);

    background-color: $mint;

    padding: 1rem;
    padding-bottom: 0.6rem;
    padding-top: 0.6rem;

    @media (min-width: $br-desktop) {
      padding: 1rem!important;
    }

    color: #fff;
    text-decoration: none;
    font-weight: bold;
    border-top-left-radius: 0.3rem;
    border-top-right-radius: 0.3rem;
  }

  @media (min-width: $br-tablet) {
    body.production {
      .role--sysadmin:not(.dashboard--screenshot-mode) {
        .header {
          &:after {
            content: 'PRODUKTION - LIVE SYSTEM - ACHTUNG - ATTENTION';
            position: absolute;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            padding-left: 2rem;
            padding-right: 2rem;
            right: 30rem;
            bottom: 0;
            font-size: 1.6rem !important;
            line-height: 6rem;
            color: $red;
            font-weight: bold;
            width: auto;
            text-align: center;
            pointer-events: none;
            animation: pulse-production 2s infinite;
            z-index: 2;
          }
        }
      }
    }
  }

  @keyframes pulse-production {
    0% {
      color: $red;
    }
    70% {
      color: lighten($red, 10%);
    }
    100% {
     color: lighten($red, 10%);
   }
  }

  .dashboard {
    &--screenshot-mode {
      #username {
        text-shadow: 0px 0px 4px #fff;
        color: rgba(0,0,0,0.0);
      }
      *:not(img).privacy--blur {
        text-shadow: 0px 0px 10px #000!important;
        color: rgba(0,0,0,0.0)!important;

        .selected-tag {
          color: rgba(0,0,0,0.0)!important;
        }

        img {
          filter: blur(5px)!important;
        }

        .dropdown-menu {
          * {
            color: rgba(0,0,0,0.0)!important;
          }
        }
      }
      img.privacy--blur {
        filter: blur(5px);
      }
      #hotel-name.privacy--blur {
        text-shadow: 0px 0px 6px #fff!important;
        color: rgba(0,0,0,0.0);
      }
    }
  }

  .privacy--blur--tooltip {
    text-shadow: 0px 0px 5px #fff;
    color: rgba(0,0,0,0.0);
  }

  .main-nav__li--update-new-version {
    display: none;
  }

  .dashboard--new-version-pending {
    .main-nav__li--update-new-version {
      display: block;
    }
  }

  .high-contrast {
    .hm-input{
      .hm-input__label {
        color: $gray!important;
      }
      &.hasContent, &.hm-input--disabled {
        .hm-input__label {
          color: $text!important;
          font-size: 1.2rem!important;
        }
      }
    }

    .hm-toggle__label, .hm-gender__label, .hm-reservation-color-picker__label, .hm-roomtype-color-picker__label {
      color: $text!important;
    }

    .calendar {
      .month-name {
        color: $text!important;
      }
    }

    .explanation-text {
      color: $text!important;
      font-style: italic;
    }
  }

  .hm-input{
    &.hasContent, &.hm-input--disabled {
      .hm-input__label {
        font-weight: 500;
      }
    }
  }

  .hm-toggle__label, .hm-gender__label, .hm-reservation-color-picker__label, .hm-roomtype-color-picker__label {
    font-weight: 500;
  }
</style>



// WEBPACK FOOTER //
// src/components/MyApp.vue